import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable } from 'rxjs';
import {
    AuthData,
    CpRadioExaminationByOpenReportModeWorklistDTO,
    ExaminationByOpenReportModeSearchWorklist,
    ExaminationWorklistDTO,
    ExecutionWorklistSearchWorklist,
    GetAdmissionWarningExaminationsParams,
    GetHomonymousExaminationsToAdmissionParams,
    GetReportExaminationsRequestWorklist,
    PrintCPRadioLabelWorklist,
    PrintCPRadioListWorklist,
    PrintCPRadioModulesWorklist,
    ReferringPendingActivitiesDTO,
    WorklistSearchAllExamsWorklist,
    WorklistSearchCPRadioApprovedWorklist,
    WorklistSearchCPRadioReportedWorklist,
    WorklistSearchCPRadioReportingWorklist,
    WorklistSearchExamsWorklist,
    WorklistWarningsDTO,
} from '@se/models';
import { ServerRootService } from '../../../utils/routing/serverroot.service';

/** Il servizio deve contenere tutte le chiamate ai metodi rest nel controller WORKLIST!
 * Ogni metodo non deve avere logica ma solo effettuare la chiamata.
 * il tipo di ritorno deve essere sempre un Observable
 */
export abstract class WorklistBaseService extends ServerRootService {
    protected readonly httpClient = inject(HttpClient);

    getExamination(idExamination: string, authData?: AuthData): Observable<ExaminationWorklistDTO> {
        return this.httpClient.get<ExaminationWorklistDTO>(
            this.getServerRoot() + '/Worklist/GetExamination/?idExamination=' + idExamination,
            this.buildOptions(authData),
        );
    }

    getWorklist(data: WorklistSearchExamsWorklist, authData?: AuthData): Observable<ExaminationWorklistDTO[]> {
        return this.httpClient.post<ExaminationWorklistDTO[]>(
            this.getServerRoot() + '/Worklist/GetWorklist',
            data,
            this.buildOptions(authData),
        );
    }

    getAllWorklist(data: WorklistSearchAllExamsWorklist, authData?: AuthData): Observable<ExaminationWorklistDTO[]> {
        return this.httpClient.post<ExaminationWorklistDTO[]>(
            this.getServerRoot() + '/Worklist/GetAllWorklist',
            data,
            this.buildOptions(authData),
        );
    }

    getWorklistCPRadioReporting(
        data: WorklistSearchCPRadioReportingWorklist,
        authData?: AuthData,
    ): Observable<ExaminationWorklistDTO[]> {
        return this.httpClient.post<ExaminationWorklistDTO[]>(
            this.getServerRoot() + '/Worklist/GetWorklistCPRadioReporting',
            data,
            this.buildOptions(authData),
        );
    }

    getWorklistCPRadioReported(
        data: WorklistSearchCPRadioReportedWorklist,
        authData?: AuthData,
    ): Observable<ExaminationWorklistDTO[]> {
        return this.httpClient.post<ExaminationWorklistDTO[]>(
            this.getServerRoot() + '/Worklist/GetWorklistCPRadioReported',
            data,
            this.buildOptions(authData),
        );
    }

    getWorklistCPRadioApproved(
        data: WorklistSearchCPRadioApprovedWorklist,
        authData?: AuthData,
    ): Observable<ExaminationWorklistDTO[]> {
        return this.httpClient.post<ExaminationWorklistDTO[]>(
            this.getServerRoot() + '/Worklist/GetWorklistCPRadioApproved',
            data,
            this.buildOptions(authData),
        );
    }

    getExecutionWorklist(
        data: ExecutionWorklistSearchWorklist,
        authData?: AuthData,
    ): Observable<ExaminationWorklistDTO[]> {
        return this.httpClient.post<ExaminationWorklistDTO[]>(
            this.getServerRoot() + '/Worklist/GetExecutionWorklist',
            data,
            this.buildOptions(authData),
        );
    }

    printCPRadioList(data: PrintCPRadioListWorklist): Observable<Blob> {
        const uri = this.getServerRoot() + '/Worklist/PrintCPRadioList';
        const options = { ...this.buildOptions(), responseType: 'blob' as 'json' };
        options.headers.set('Accept', 'application/pdf');
        return this.httpClient.post<Blob>(uri, data, options);
    }

    getCpRadioExaminationsByOpenReportMode(
        data: ExaminationByOpenReportModeSearchWorklist,
        authData?: AuthData,
    ): Observable<CpRadioExaminationByOpenReportModeWorklistDTO[]> {
        return this.httpClient.post<CpRadioExaminationByOpenReportModeWorklistDTO[]>(
            this.getServerRoot() + '/Worklist/GetCpRadioExaminationsByOpenReportMode',
            data,
            this.buildOptions(authData),
        );
    }

    printCPRadioLabels(data: PrintCPRadioLabelWorklist): Observable<Blob> {
        const uri = this.getServerRoot() + '/Worklist/PrintCPRadioLabels';
        const options = { ...this.buildOptions(), responseType: 'blob' as 'json' };
        options.headers.set('Accept', 'application/pdf');
        return this.httpClient.post<Blob>(uri, data, options);
    }

    printCPRadioTemplateCustom(data: PrintCPRadioModulesWorklist): Observable<Blob> {
        const uri = this.getServerRoot() + '/Worklist/PrintCPRadioTemplateCustom';
        const options = { ...this.buildOptions(), responseType: 'blob' as 'json' };
        options.headers.set('Accept', 'application/pdf');
        return this.httpClient.post<Blob>(uri, data, options);
    }

    getHomonymousExaminationsToSendToWorklist(
        idExamination: string,
        authData?: AuthData,
    ): Observable<ExaminationWorklistDTO[]> {
        return this.httpClient.get<ExaminationWorklistDTO[]>(
            this.getServerRoot() +
                '/Worklist/GetHomonymousExaminationsToSendToWorklist/?idExamination=' +
                idExamination,
            this.buildOptions(authData),
        );
    }

    getHomonymousExaminationsToAdmission(
        params: GetHomonymousExaminationsToAdmissionParams,
        authData?: AuthData,
    ): Observable<ExaminationWorklistDTO[]> {
        return this.httpClient.post<ExaminationWorklistDTO[]>(
            this.getServerRoot() + '/Worklist/GetHomonymousExaminationsToAdmission',
            params,
            this.buildOptions(authData),
        );
    }

    getAdmissionWarningExaminations( params: GetAdmissionWarningExaminationsParams, authData?: AuthData): Observable<ExaminationWorklistDTO[]> {
        return this.httpClient.post<ExaminationWorklistDTO[]>(
            this.getServerRoot() + '/Worklist/GetAdmissionWarningExaminations',
            params,
            this.buildOptions(authData),
        )
    }

    getHomonymousExaminationsToExecution(
        idExamination: string,
        authData?: AuthData,
    ): Observable<ExaminationWorklistDTO[]> {
        return this.httpClient.get<ExaminationWorklistDTO[]>(
            this.getServerRoot() + '/Worklist/GetHomonymousExaminationsToExecution/?idExamination=' + idExamination,
            this.buildOptions(authData),
        );
    }

    getReportExaminations(
        data: GetReportExaminationsRequestWorklist,
        authData?: AuthData,
    ): Observable<ExaminationWorklistDTO[]> {
        return this.httpClient.post<ExaminationWorklistDTO[]>(
            this.getServerRoot() + '/Worklist/GetReportExaminations',
            data,
            this.buildOptions(authData),
        );
    }

    getNumberOfDeferredReports(): Observable<WorklistWarningsDTO> {
        return this.httpClient.get<WorklistWarningsDTO>(
            this.getServerRoot() + '/Worklist/GetNumberOfDeferredReports',
            this.buildOptions(),
        );
    }

    getPendingActivitiesForCpReporting(): Observable<ReferringPendingActivitiesDTO[]> {
        return this.httpClient.get<ReferringPendingActivitiesDTO[]>(
            this.getServerRoot() + '/Worklist/GetPendingActivitiesForCpReporting',
            this.buildOptions(),
        );
    }
}
